import { Box, SxProps, useTheme } from '@mui/material';
import {
    DateRange,
    DateRangePickerDay,
    DateRangePickerDayProps,
    LocalizationProvider,
    StaticDateRangePicker as DateRangePicker,
    DateRangePickerProps,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { tzdate, TZDate } from '@repo/tzdate';
import { useCallback, useMemo, useState, useEffect, type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import { Dayjs } from 'dayjs';
import { getWeekNumbersForMonth } from '../../utils/dateUtils';
import WeekNumbersColumn from '../calendar/subcomponents/WeekNumbersColumn';

interface IProps {
    dateRange: DateRange<TZDate>;
    onChange: (newDateRange: DateRange<TZDate>, context?: any) => void;
    date?: string;
    minDate?: TZDate;
    maxDate?: TZDate;
    isDateUnavailable?: (date: string) => boolean;
    onMonthChange?: (date: Dayjs | TZDate | null) => void;
    loading?: boolean;
    dateRangeVariant?: 'days' | 'nights';
    renderCustomDay?: (date: TZDate) => JSX.Element;
    customDaySx?: SxProps;
    showWeekNumbers?: boolean;
    weekNumberRowHeight?: number;
    weekNumberRowMargin?: number;
    weekNumberRowMarginBottom?: number;
    weekNumberTopPadding?: number;
}

function allDatesBetweenAreAvailable(
    day1: TZDate,
    day2: TZDate,
    isDateUnavailable?: (date: string) => boolean,
) {
    let day = tzdate(day1);
    if (!isDateUnavailable) return true;

    while (day.isBefore(day2)) {
        if (isDateUnavailable(day.format('YYYY-MM-DD'))) return false;
        day = day.add(1, 'days');
    }

    return true;
}

const DateRangeSlotOverrides = {
    actionBar: () => null,
};

export default function StaticDateRangePicker(
    props: IProps & Partial<DateRangePickerProps>,
): JSX.Element {
    const {
        date,
        dateRange,
        onChange,
        minDate,
        maxDate,
        isDateUnavailable,
        onMonthChange,
        loading,
        dateRangeVariant = 'days',
        renderCustomDay,
        customDaySx,
        showWeekNumbers = false,
        weekNumberRowHeight = 40,
        weekNumberRowMargin,
        weekNumberRowMarginBottom,
        weekNumberTopPadding = 16,
        ...rest
    } = props;

    const { t, dayjsLocale } = useLocale();
    const theme = useTheme();
    const [currentMonth, setCurrentMonth] = useState<Dayjs>(
        dateRange[0]?.forceLocalTz().getDayjsDate() ?? tzdate().getDayjsDate(),
    );

    useEffect(() => {
        if (dateRange[0]) {
            setCurrentMonth(dateRange[0].forceLocalTz().getDayjsDate());
        }
    }, [dateRange]);

    const weekNumbers = useMemo(() => {
        if (!showWeekNumbers) return [];
        return getWeekNumbersForMonth(currentMonth);
    }, [showWeekNumbers, currentMonth]);

    const textColor = useMemo(() => theme.palette.text.secondary, [theme.palette.text.secondary]);

    const shouldDisableDate = useCallback(
        (day: Dayjs | TZDate) => {
            const d = day instanceof TZDate ? day.getDayjsDate() : day;
            const date = tzdate(d);
            if (date.isSame(dateRange[0], 'day')) {
                return true;
            } else if (isDateUnavailable) {
                if (isDateUnavailable && (dateRange[1] !== null || dateRange[0] === null)) {
                    return isDateUnavailable(date.format('YYYY-MM-DD'));
                } else {
                    if (date?.isBefore(dateRange[0])) {
                        return isDateUnavailable(date.format('YYYY-MM-DD'));
                    } else if (dateRange[0] && date) {
                        return !allDatesBetweenAreAvailable(dateRange[0], date, isDateUnavailable);
                    }
                }
            }
            return false;
        },
        [dateRange, isDateUnavailable],
    );

    const defaultMonth = useMemo(
        () => (minDate && minDate.isAfter(tzdate(date)) ? minDate : tzdate(date)),
        [date, minDate],
    );
    const selectedDatesInUserTimezone = useMemo(() => {
        return dateRange.map((date) => {
            if (date) {
                return date.forceLocalTz().getDayjsDate();
            }
            return null;
        }) as DateRange<Dayjs>;
    }, [dateRange]);

    const onChangeDateRange = useCallback(
        (range: DateRange<Dayjs | TZDate>) => {
            onChange([
                range[0] ? (range[0] instanceof TZDate ? range[0] : tzdate(range[0])) : null,
                range[1] ? (range[1] instanceof TZDate ? range[1] : tzdate(range[1])) : null,
            ] as DateRange<TZDate>);
        },
        [onChange],
    );

    const handleMonthChange = useCallback(
        (month: Dayjs | TZDate) => {
            const d = month instanceof TZDate ? month.getDayjsDate() : month;
            setCurrentMonth(d);
            if (onMonthChange) {
                onMonthChange(month instanceof TZDate ? month : tzdate(month));
            }
        },
        [onMonthChange],
    );

    const renderDay = useCallback(
        (date: Dayjs, dateRangePickerDayProps: DateRangePickerDayProps) =>
            getRenderDay(
                dateRangePickerDayProps,
                isDateUnavailable,
                tzdate(date),
                dateRangeVariant,
                dateRange,
                renderCustomDay,
                customDaySx,
            ),
        [isDateUnavailable, dateRangeVariant, renderCustomDay, customDaySx, dateRange],
    );

    const dateRangePicker = (
        <DateRangePicker
            {...rest}
            value={selectedDatesInUserTimezone}
            disablePast={!minDate}
            autoFocus={true}
            onChange={onChangeDateRange}
            minDate={minDate?.forceLocalTz().getDayjsDate()}
            maxDate={maxDate?.forceLocalTz().getDayjsDate()}
            referenceDate={defaultMonth.forceLocalTz().getDayjsDate()}
            disableHighlightToday={true}
            shouldDisableDate={shouldDisableDate}
            onMonthChange={handleMonthChange}
            loading={loading}
            slots={{
                ...DateRangeSlotOverrides,
                toolbar: () => null,
                day: (dayProps) => renderDay(dayProps.day as Dayjs, dayProps),
            }}
        />
    );

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={dayjsLocale}
            localeText={{
                start: capitalize(t.date_range_start),
                end: capitalize(t.date_range_end),
            }}
        >
            {showWeekNumbers ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        width: '100%',
                    }}
                >
                    <WeekNumbersColumn
                        weekNumbers={weekNumbers}
                        textColor={textColor}
                        rowHeight={weekNumberRowHeight}
                        rowMargin={weekNumberRowMargin}
                        rowMarginBottom={weekNumberRowMarginBottom}
                        topPadding={weekNumberTopPadding}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>{dateRangePicker}</Box>
                </Box>
            ) : (
                <Box>{dateRangePicker}</Box>
            )}
        </LocalizationProvider>
    );
}

function getRenderDay(
    { key, ...dateRangePickerDayProps }: DateRangePickerDayProps,
    isDateUnavailable: ((date: string) => boolean) | undefined,
    date: TZDate,
    dateRangeVariant: 'days' | 'nights',
    selectedDates: DateRange<TZDate>,
    renderCustomDay?: (date: TZDate) => JSX.Element,
    customDaySx?: SxProps,
) {
    if (
        dateRangePickerDayProps.selected ||
        dateRangePickerDayProps.isPreviewing ||
        dateRangePickerDayProps.outsideCurrentMonth ||
        dateRangePickerDayProps.isHighlighting || //highligted
        !isDateUnavailable || // no availabilityFunction
        (!isDateUnavailable(date.format('YYYY-MM-DD')) &&
            !isDateUnavailable(date.subtract(1, 'day').format('YYYY-MM-DD'))) ||
        dateRangeVariant === 'days'
    ) {
        if (renderCustomDay) {
            return (
                <DateRangePickerDay
                    tabIndex={0}
                    key={key}
                    {...dateRangePickerDayProps}
                    sx={customDaySx}
                >
                    {renderCustomDay(date)}
                </DateRangePickerDay>
            );
        }

        const [start, end] = selectedDates;
        const selected = date.isSame(start, 'day') || date.isSame(end, 'day');

        const isStartOfHighlighting = date.isSame(start, 'day');
        const isEndOfHighlighting = date.isSame(end, 'day');
        const isHighlighting =
            (isStartOfHighlighting && end !== null) ||
            isEndOfHighlighting ||
            (date.isAfter(start, 'day') && date.isBefore(end, 'day'));

        return (
            <DateRangePickerDay
                tabIndex={0}
                key={key}
                {...dateRangePickerDayProps}
                selected={selected}
                isStartOfHighlighting={isStartOfHighlighting}
                isEndOfHighlighting={isEndOfHighlighting}
                isHighlighting={isHighlighting}
            />
        );
    } else {
        return (
            <DateRangePickerDay
                tabIndex={0}
                key={key}
                {...dateRangePickerDayProps}
                sx={{
                    position: 'relative',
                    '&:hover, &:focus, &:active': {
                        '& .bilberry-period-unavailable-edge': {
                            background: 'unset',
                        },
                    },
                    ...(customDaySx || {}),
                }}
            >
                {!isDateUnavailable(date.subtract(1, 'day').format('YYYY-MM-DD')) ? (
                    <StartOfUnavailablePeriod />
                ) : !isDateUnavailable(date.format('YYYY-MM-DD')) ? (
                    <EndOfUnavailablePeriod />
                ) : (
                    <UnavailablePeriod />
                )}
                {renderCustomDay
                    ? renderCustomDay(date)
                    : dateRangePickerDayProps.day.date().toString()}
            </DateRangePickerDay>
        );
    }
}

function StartOfUnavailablePeriod() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                zIndex: -1,
                position: 'absolute',
                // Anchor to the cell's left edge. Without `left: 0` the
                // absolute box's static position is offset by ~2px, bleeding
                // into the previous cell. For middle-of-row cells the bleed
                // is masked by the previous cell's own right overflow; for
                // the first cell of a row nothing masks it and it shows as a
                // small nub sticking into the week-numbers gutter (most
                // visible on dark surfaces).
                left: 0,
                width: '107%',
                height: '75%',
                top: '50%',
                transform: 'translateY(-50%) !important',
            }}
        >
            <Box
                className="bilberry-period-unavailable-edge"
                sx={{
                    display: 'inline-block',
                    ml: '25%',
                    width: '50%',
                    height: '100%',
                    background: `linear-gradient(to bottom right, transparent 50%,${theme.palette.grey[100]} 50%);`,
                    '&:hover,&focus,&active': {
                        background: 'unset',
                    },
                }}
            />
            <Box
                className="bilberry-period-unavailable-edge"
                sx={{
                    display: 'inline-block',
                    width: '25%',
                    height: '100%',
                    background: theme.palette.grey[100],
                    '&:hover,&focus,&active': {
                        background: 'unset',
                    },
                }}
            />
        </Box>
    );
}

function EndOfUnavailablePeriod() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                zIndex: -1,
                position: 'absolute',
                // See StartOfUnavailablePeriod for why this is needed.
                left: 0,
                width: '107%',
                height: '75%',
                top: '50%',
                transform: 'translateY(-50%) !important',
            }}
        >
            <Box
                className="bilberry-period-unavailable-edge"
                sx={{
                    display: 'inline-block',
                    width: '25%',
                    height: '100%',
                    background: theme.palette.grey[100],
                }}
            />
            <Box
                className="bilberry-period-unavailable-edge"
                sx={{
                    display: 'inline-block',
                    mr: '25%',
                    width: '50%',
                    height: '100%',
                    background: `linear-gradient(to top left, transparent 50%,${theme.palette.grey[100]} 50%);`,
                }}
            />
        </Box>
    );
}

function UnavailablePeriod() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                zIndex: -1,
                position: 'absolute',
                // See StartOfUnavailablePeriod for why this is needed.
                left: 0,
                background: theme.palette.grey[100],
                width: '107%',
                height: '75%',
                top: '50%',
                transform: 'translateY(-50%) !important',
            }}
        ></Box>
    );
}
